// Demo page: NextAdmin /auth/two-step-verification (single-purpose card) and /mail-success (outcome with one action).
import { useMutation } from '@tanstack/react-query';
import { CheckCircle, Mail, XCircle } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { verifyEmailMutation } from '../generated/api/@tanstack/react-query.gen.js';
import { AuthLayout } from '../components/page/AuthLayout.js';
import { buttonStyles } from '../components/tailgrids/core/button.js';
import { Spinner } from '../components/tailgrids/core/spinner/index.js';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const verifyToken = searchParams.get('token') ?? '';
  const verify = useMutation(verifyEmailMutation());
  // The token is single-use: React StrictMode's second effect run must not submit it again.
  const submitted = useRef(false);

  useEffect(() => {
    if (!verifyToken || submitted.current) return;
    submitted.current = true;
    verify.mutate({ body: { token: verifyToken } });
  }, [verifyToken, verify]);

  const homeLink = (label: string) => (
    <Link to="/" className={buttonStyles({ size: 'lg', className: 'w-full' })}>
      {label}
    </Link>
  );

  if (!verifyToken || verify.isError) {
    return (
      <AuthLayout
        title="We could not verify your email"
        subtitle={
          verifyToken
            ? 'This link is invalid, expired or has already been used. Request a new verification email from your profile.'
            : 'The link is missing its token. Open the link from your email again.'
        }
      >
        <div className="flex flex-col items-center gap-6">
          <span className="flex size-14 items-center justify-center rounded-full bg-danger-muted text-danger-text">
            <XCircle size={28} />
          </span>
          {homeLink('Go to Conflui')}
        </div>
      </AuthLayout>
    );
  }

  if (verify.isSuccess) {
    return (
      <AuthLayout title="Email verified" subtitle="Thanks, your email address is confirmed.">
        <div className="flex flex-col items-center gap-6">
          <span className="flex size-14 items-center justify-center rounded-full bg-success-muted text-success-text">
            <CheckCircle size={28} />
          </span>
          {homeLink('Continue to Conflui')}
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Verifying your email" subtitle="This takes a moment.">
      <div className="flex flex-col items-center gap-4 text-text-tertiary" role="status">
        <Mail size={28} />
        <Spinner size="md" />
      </div>
    </AuthLayout>
  );
}
